/**
 * External worklist CPT / drug table: shows concatenated auth evidences and a
 * read-only {@link CustomTable} of J-codes for the loaded order.
 *
 * @module singleOrderForExternal/components/drugTableForExternal/drugTableForExternal
 */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { useFacilityCategory } from "../../../../hooks/useOncoemrTasksEnabled";
import { RootState } from "../../../../redux/store/store";
import {
  generateDrugTableForExternalData,
  getDrugTableForExternalColumnsKeys,
} from "./utils/generateTableDataForExternal";

/**
 * Tab content that renders auth-on-file evidence text and the external drug table.
 *
 * @returns Loader or evidence block plus {@link CustomTable}
 */
const DrugTableForExternal = () => {
  const { orderId } = useParams();
  const { authOnFileDetails, authOnFileDetailsLoading: isLoading } =
    useDataAccumulatorForSingleOrder();

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const [tableData, setTableData] = useState<any[]>([]);

  // Build columns at render time so the org-dependent J-codes column label
  // ("CPT Codes" for Astera Radiology vs. "J Codes" elsewhere) reflects the
  // facility id resolved after auth/token initialization. Memoized to avoid
  // rebuilding the header array on every render.
  const facilityCategory = useFacilityCategory();
  const tableHeaders = useMemo(
    () => getDrugTableForExternalColumnsKeys(facilityCategory),
    [facilityCategory],
  );

  useEffect(() => {
    if (authOnFileDetails) {
      let generatedData = generateDrugTableForExternalData(
        authOnFileDetails?.auth_on_file ?? {},
        authStatusOptions ?? [],
      );
      setTableData(generatedData);
    }
  }, [authOnFileDetails, orderId]);

  return (
    <div className="cpt-codes-table--container flex h-full flex-col">
      {isLoading ? (
        <LoaderMessage message={"Fetching CPT Codes"} />
      ) : (
        <>
          <div className="table-drug-template h-full">
            <CustomTable
              tableHeaders={tableHeaders}
              tableData={tableData}
              itemsPerPage={tableData.length}
              pagesPerView={5}
              count={tableData.length}
              onRowClick={() => {}}
              tableName="cptCodes"
              hideSearchBar={true}
              hidePagination={true}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DrugTableForExternal;
